package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

sealed class TransactionsScreenState {
    data object Loading : TransactionsScreenState()
    data class Error(val message: String) : TransactionsScreenState()
    data class Loaded(
        val transactions: List<Transaction>,
        val accountNames: Map<String, String> = emptyMap(),
        val institutionNames: Map<String, String?> = emptyMap()
    ) : TransactionsScreenState()
}

class TransactionsViewModel(
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val plaidItemsEventBus: PlaidItemsEventBus
) : ViewModel() {

    private val _state = MutableStateFlow<TransactionsScreenState>(TransactionsScreenState.Loading)
    val state = _state.asStateFlow()

    init {
        load()
        viewModelScope.launch {
            plaidItemsEventBus.events.collect {
                load()
            }
        }
    }

    fun load() {
        _state.value = TransactionsScreenState.Loading
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        viewModelScope.launch {
            combine(
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems(),
                transactionRepository.transactions
            ) { accountsResponse, plaidItemsResponse, transactionsResponse ->
                when {
                    accountsResponse is Response.Error ->
                        TransactionsScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error ->
                        TransactionsScreenState.Error(plaidItemsResponse.message)
                    transactionsResponse is Response.Error ->
                        TransactionsScreenState.Error(transactionsResponse.message)
                    accountsResponse is Response.Success && plaidItemsResponse is Response.Success && transactionsResponse is Response.Success -> {
                        if (accountsResponse.data.isEmpty()) {
                            TransactionsScreenState.Loaded(emptyList())
                        } else {
                            val accountNames = mutableMapOf<String, String>()
                            val institutionNames = mutableMapOf<String, String?>()

                            val plaidItems = plaidItemsResponse.data
                            accountsResponse.data.forEach { acc ->
                                accountNames[acc.accountId] = acc.name
                                val institutionName = plaidItems.find { it.itemId == acc.itemId }?.institutionName
                                institutionNames[acc.accountId] = institutionName
                            }

                            TransactionsScreenState.Loaded(
                                transactions = transactionsResponse.data,
                                accountNames = accountNames,
                                institutionNames = institutionNames
                            )
                        }
                    }
                    else -> TransactionsScreenState.Loading
                }
            }.collect { _state.value = it }
        }
    }
}
